/**
 * The board.
 *
 * One SVG holding every element, with each element a focusable node carrying
 * its own accessible name. That last part is why this is SVG and not canvas: a
 * family tree a therapist can only reach with a mouse is a family tree half
 * their patients cannot use.
 *
 * The canvas fills whatever it is given and never grows the page. Dragging
 * empty space pans, dragging an element moves it, and the wheel zooms — the
 * gestures every board tool has trained people to expect. A board that made
 * the page scroll instead would put the thing you are drawing on underneath
 * the thing you are scrolling.
 *
 * Selection is held by the caller, because the rail and the inspector need it
 * too — the canvas reports what was picked and draws the consequence.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Maximize, Minus, Plus } from "lucide-react";

import {
    NODE_RADIUS,
    centreOf,
    childPath,
    fitViewBox,
    linkEndpoints,
    trimSegment,
    unionGeometry,
    type Point,
} from "../../domain/layout";
import type { RemoteCursor } from "../../application/useBoardPresence";
import { PRESENCE_INKS } from "../../domain/presenceInk";
import {
    CENTRE_NODE_ID,
    COLOUR_INK,
    DEFAULT_CENTRE_RADIUS,
    DEFAULT_SYSTEM_RADIUS,
    MAX_CENTRE_RADIUS,
    MAX_SYSTEM_RADIUS,
    MIN_CENTRE_RADIUS,
    MIN_SYSTEM_RADIUS,
    type ElementColour,
    isCentre,
    isNote,
    isPerson,
    isSystem,
    type Attribution,
    type MapLink,
    type MapNode,
    type MapState,
} from "../../domain/types";
import {
    ArrowMarkers,
    CentreShape,
    DeceasedCross,
    EmotionalLine,
    HouseholdBoundary,
    IndexRing,
    NoteCard,
    NoteOutline,
    PersonShape,
    Sibship,
    SystemTie,
    SystemShape,
    UnionLine,
    type SibshipMember,
} from "./MapGlyphs";

export type SelectionId = string | null;

/** Who is looking, so the board can say which marks are the other person's. */
export interface Viewer {
    type: "therapist" | "participant";
    id?: string;
}

interface Props {
    state: MapState;
    selected: SelectionId;
    /** The second element of a pending link, while one is being drawn. */
    linking: SelectionId;
    viewer: Viewer;
    onSelect: (id: SelectionId) => void;
    onMove: (id: string, x: number, y: number) => void;
    /**
     * Where the middle of the board currently is, so whoever places the next
     * element can put it where the person is actually looking.
     */
    onViewCentreChange?: (centre: Point) => void;
    /** Where everyone else is pointing, in board units. */
    cursors?: RemoteCursor[];
    /**
     * Who is which colour, assigned once for everyone on the board.
     *
     * Passed in rather than worked out from the cursors, because the cursors
     * are everyone *else*: assigning from them would give the two sides two
     * different answers about who is green.
     */
    inks?: Record<string, string>;
    /** A circle was dragged to a new size. Absent where nothing may change. */
    onResize?: (node: MapNode, radius: number) => void;
    /** Say where this pointer is. Called on every move; throttled downstream. */
    onPointerAt?: (x: number, y: number) => void;
    /** Off for a read-only view; the board still pans, zooms and reads out. */
    editable?: boolean;
}

/** A therapist's own colour, or nothing — which leaves the board's own ink. */
const inkOf = (colour: ElementColour | undefined): string | undefined =>
    colour && colour !== "default" ? COLOUR_INK[colour] : undefined;

/**
 * How far a tie stops short of what it joins.
 *
 * The centre is drawn larger than a system, so the gap is read from the node
 * rather than assumed — a fixed gap would either cut into the smaller shape or
 * leave a floating arrow beside the larger one.
 */
const gapAt = (nodeId: string, nodes: Map<string, MapNode>): number => {
    const node = nodes.get(nodeId);
    // The ring, not a dot: a tie that stopped at the centre point would cross
    // the whole household to get there. A system that was drawn large has the
    // same claim on its own edge.
    return node ? radiusOf(node) + 6 : NODE_RADIUS + 6;
};

/** How far a grab may sit from the rim and still count as being on it. */
const GRIP_LIMIT = 24;

/** How big a circle is, whoever is asking. */
const radiusOf = (node: MapNode): number => {
    if (isCentre(node)) return node.radius ?? DEFAULT_CENTRE_RADIUS;
    if (isSystem(node)) return node.radius ?? DEFAULT_SYSTEM_RADIUS;
    return NODE_RADIUS;
};

const ZOOM_STEP = 1.25;
const MIN_SCALE = 0.3;
const MAX_SCALE = 3;

export default function MapCanvas({
    state,
    selected,
    linking,
    viewer,
    onSelect,
    onMove,
    onViewCentreChange,
    cursors = [],
    inks,
    onResize,
    onPointerAt,
    editable = true,
}: Props) {
    const { t } = useTranslation();
    const markerPrefix = useId().replace(/:/g, "");
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [scale, setScale] = useState(1);
    const [pan, setPan] = useState<Point>({ x: 0, y: 0 });
    const drag = useRef<{ id: string; offset: Point } | null>(null);
    const resize = useRef<{
        id: string;
        at: Point;
        /** Pointer-to-rim distance at the grab, kept for the whole drag. */
        grip: number;
        radius: number;
    } | null>(null);
    /** The size being dragged out, before it is committed as one edit. */
    const [sizing, setSizing] = useState<{ id: string; radius: number } | null>(null);
    const panning = useRef<{ from: Point; origin: Point } | null>(null);

    const nodesById = useMemo(
        () => new Map(state.nodes.map((node) => [node.id, node])),
        [state.nodes],
    );
    const linksById = useMemo(
        () => new Map(state.links.map((link) => [link.id, link])),
        [state.links],
    );
    /**
     * The window onto the board, in board units.
     *
     * Held in state and *not* recomputed from the elements on every render.
     * It used to be: `fitViewBox` ran on each change, so dragging a figure
     * moved the window it was being measured against, the pointer and the
     * board fought each other, and the coordinates ran away until the server
     * rejected them as out of range. The view belongs to the person looking
     * at it — it fits once, and after that it moves when they move it.
     */
    const [base, setBase] = useState(() => fitViewBox(state.nodes));
    const fitted = useRef(false);

    const view = useMemo(
        () => ({
            x: base.x + pan.x,
            y: base.y + pan.y,
            width: base.width / scale,
            height: base.height / scale,
        }),
        [base, pan, scale],
    );

    // Fit once, on the way in: opening a map that already has a family on it
    // should show the family. Fitting again later would move the board out
    // from under whoever is working on it — including the moment they place
    // their first figure.
    useEffect(() => {
        if (fitted.current) return;
        fitted.current = true;
        if (state.nodes.length > 0) setBase(fitViewBox(state.nodes));
    }, [state.nodes]);

    useEffect(() => {
        onViewCentreChange?.({
            x: view.x + view.width / 2,
            y: view.y + view.height / 2,
        });
    }, [view, onViewCentreChange]);

    /** Screen pixels to board units, so a drag tracks the pointer at any zoom. */
    const toBoard = useCallback(
        (event: { clientX: number; clientY: number }): Point => {
            const svg = svgRef.current;
            if (!svg) return { x: 0, y: 0 };
            const rect = svg.getBoundingClientRect();
            return {
                x: view.x + ((event.clientX - rect.left) / rect.width) * view.width,
                y: view.y + ((event.clientY - rect.top) / rect.height) * view.height,
            };
        },
        [view],
    );

    /**
     * Dragging a circle's edge changes how big it is.
     *
     * Held locally until the pointer is let go: the size is one edit, not one
     * per frame, and a board sent sixty upserts a second would spend the
     * session catching up rather than drawing.
     *
     * The grab records how far the pointer was from the rim when it took hold,
     * and keeps that distance for the whole drag. Without it the circle jumps
     * to wherever the pointer happened to be on the first frame — grabbing the
     * rim a few pixels wide snapped the size by those few pixels — and the rim
     * then trails the pointer instead of following it.
     */
    const startResize = (event: React.PointerEvent, node: MapNode) => {
        if (!editable) return;
        event.stopPropagation();
        event.preventDefault();

        const at = toBoard(event);
        const radius = radiusOf(node);
        const held = Math.hypot(at.x - node.x, at.y - node.y);
        resize.current = {
            id: node.id,
            at: { x: node.x, y: node.y },
            // Bounded: the target is deliberately larger than the mark, and a
            // grab at its outer edge should still behave like a grab on the
            // rim rather than carrying that whole offset for the drag.
            grip: Math.max(-GRIP_LIMIT, Math.min(GRIP_LIMIT, radius - held)),
            radius,
        };
        setSizing({ id: node.id, radius });
        (event.currentTarget as Element).setPointerCapture?.(event.pointerId);
    };

    const continueResize = (event: React.PointerEvent): boolean => {
        const held = resize.current;
        if (!held) return false;

        const bounds =
            held.id === CENTRE_NODE_ID
                ? [MIN_CENTRE_RADIUS, MAX_CENTRE_RADIUS]
                : [MIN_SYSTEM_RADIUS, MAX_SYSTEM_RADIUS];
        const at = toBoard(event);
        const distance = Math.hypot(at.x - held.at.x, at.y - held.at.y) + held.grip;
        const radius = Math.round(Math.min(bounds[1], Math.max(bounds[0], distance)));

        // Kept on the grip as well as in state: the pointer can be let go on
        // the same frame as the last move, and reading it back from a render
        // that has not happened yet would file the size before last.
        held.radius = radius;
        setSizing({ id: held.id, radius });
        return true;
    };

    const endResize = () => {
        const held = resize.current;
        resize.current = null;
        setSizing(null);
        if (!held) return;

        const node = nodesById.get(held.id);
        if (!node || radiusOf(node) === held.radius) return;
        onResize?.(node, held.radius);
    };

    const startDrag = (event: React.PointerEvent, node: MapNode) => {
        if (!editable) return;
        const at = toBoard(event);
        drag.current = { id: node.id, offset: { x: at.x - node.x, y: at.y - node.y } };
        (event.target as Element).setPointerCapture?.(event.pointerId);
    };

    /** Dragging empty space moves the board under the pointer, not the page. */
    const startPan = (event: React.PointerEvent) => {
        panning.current = {
            from: { x: event.clientX, y: event.clientY },
            origin: { ...pan },
        };
        (event.currentTarget as Element).setPointerCapture?.(event.pointerId);
    };

    const continueDrag = (event: React.PointerEvent) => {
        // Reported on every move, before anything else: the other side should
        // see the pointer travel even when it is not dragging anything.
        if (onPointerAt) {
            const at = toBoard(event);
            onPointerAt(at.x, at.y);
        }
        if (continueResize(event)) return;
        if (drag.current) {
            const at = toBoard(event);
            onMove(
                drag.current.id,
                Math.round(at.x - drag.current.offset.x),
                Math.round(at.y - drag.current.offset.y),
            );
            return;
        }
        if (!panning.current) return;

        const svg = svgRef.current;
        if (!svg) return;
        const rect = svg.getBoundingClientRect();
        const dx = ((event.clientX - panning.current.from.x) / rect.width) * view.width;
        const dy = ((event.clientY - panning.current.from.y) / rect.height) * view.height;
        setPan({
            x: panning.current.origin.x - dx,
            y: panning.current.origin.y - dy,
        });
    };

    const endDrag = () => {
        endResize();
        drag.current = null;
        panning.current = null;
    };

    /**
     * The pointer left the board.
     *
     * A drag of the board or of a figure stops; a resize does not. The rim is
     * pulled past the edge of the canvas all the time — that is what making
     * something bigger looks like — and the handle holds the pointer until it
     * is let go.
     */
    const leaveBoard = () => {
        if (resize.current) return;
        drag.current = null;
        panning.current = null;
    };

    /** The wheel zooms, the way every board tool does. */
    const onWheel = (event: React.WheelEvent) => {
        if (event.deltaY === 0) return;
        setScale((current) =>
            Math.min(
                MAX_SCALE,
                Math.max(MIN_SCALE, current * (event.deltaY < 0 ? 1.08 : 1 / 1.08)),
            ),
        );
    };

    /**
     * Keyboard nudging.
     *
     * Not a convenience: dragging is the only other way to place an element,
     * and a therapist who cannot use a pointer would otherwise have a board
     * they can read and never build.
     */
    const nudge = (event: React.KeyboardEvent, node: MapNode) => {
        if (!editable) return;
        const step = event.shiftKey ? 40 : 10;
        const moves: Record<string, Point> = {
            ArrowUp: { x: 0, y: -step },
            ArrowDown: { x: 0, y: step },
            ArrowLeft: { x: -step, y: 0 },
            ArrowRight: { x: step, y: 0 },
        };
        const delta = moves[event.key];
        if (!delta) return;
        event.preventDefault();
        onMove(node.id, node.x + delta.x, node.y + delta.y);
    };

    const nodeLabel = (node: MapNode): string => {
        if (isPerson(node)) {
            const name = node.name || t("systemicMap.canvas.unnamedPerson");
            const parts = [name, t(`systemicMap.gender.${node.gender}`)];
            if (node.birthYear) parts.push(String(node.birthYear));
            if (node.deceased) parts.push(t("systemicMap.person.deceased"));
            if (node.index) parts.push(t("systemicMap.person.indexPatient"));
            return parts.join(", ");
        }
        if (isSystem(node)) {
            return node.label || t(`systemicMap.system.${node.system}`);
        }
        if (isNote(node)) {
            // Read out as what it says and whose it is, because that is what a
            // note is: someone's words on the board.
            const text = node.text.trim() || t("systemicMap.note.empty");
            return node.author
                ? t("systemicMap.note.byLabel", { text, author: node.author })
                : text;
        }
        return node.label || t("systemicMap.canvas.centre");
    };

    /**
     * A drawn relationship names itself as a relationship, not just as its
     * type: "married" alone would be indistinguishable from the palette button
     * that creates one, both to a screen reader and to a test.
     */
    const linkLabel = (link: MapLink): string => {
        const relation =
            link.type === "union"
                ? t(`systemicMap.union.${link.union}`)
                : link.type === "child"
                  ? t(`systemicMap.child.${link.child}`)
                  : link.type === "emotional"
                    ? t(`systemicMap.emotional.${link.emotional}`)
                    : `${t(`systemicMap.strength.${link.strength}`)}, ${t(
                          `systemicMap.energy.${link.energy}`,
                      )}`;
        return t("systemicMap.canvas.linkLabel", { relation });
    };

    /**
     * Whose mark this is, when it is not the viewer's own.
     *
     * A shared board where you cannot tell who moved the grandmother is a
     * board two people end up arguing over. The name sits beside the element,
     * the way a collaborative canvas labels the other person's cursor.
     */
    const otherHand = (element: Attribution): string | null => {
        if (!element.updatedBy) return null;
        const isMine =
            viewer.id !== undefined
                ? element.updatedBy === viewer.id
                : element.updatedByType === viewer.type;
        if (isMine) return null;
        return (
            element.updatedByName ||
            t(
                element.updatedByType === "therapist"
                    ? "systemicMap.canvas.byTherapist"
                    : "systemicMap.canvas.byPatient",
            )
        );
    };

    /**
     * The children of each couple, grouped.
     *
     * A child link is drawn as part of its sibship rather than on its own, so
     * the group has to be assembled before anything is painted.
     */
    /**
     * Everyone whose own line says the pregnancy was lost early.
     *
     * Read from the child link rather than from the person, because it is the
     * filiation that carries what happened — the figure only shows it.
     */
    const miscarried = useMemo(
        () =>
            new Set(
                state.links
                    .filter((link) => link.type === "child" && link.child === "miscarriage")
                    .map((link) => link.to),
            ),
        [state.links],
    );

    const sibships = useMemo(() => {
        const groups = new Map<string, { anchor: Point; members: SibshipMember[] }>();

        for (const link of state.links) {
            if (link.type !== "child") continue;
            const ends = linkEndpoints(link, nodesById, linksById);
            if (!ends) continue;
            const [anchor, at] = ends;

            const group = groups.get(link.from) ?? { anchor, members: [] };
            group.members.push({
                id: link.id,
                kind: link.child,
                at,
                selected: selected === link.id,
                ink: inkOf(link.colour),
                label: linkLabel(link),
                onSelect: () => onSelect(link.id),
            });
            groups.set(link.from, group);
        }

        return [...groups.values()];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.links, nodesById, linksById, selected, t]);

    const householdPoints = (memberIds: string[]): Point[] =>
        memberIds
            .map((id) => nodesById.get(id))
            .filter((node): node is MapNode => Boolean(node))
            .map(centreOf);

    return (
        <div className="relative h-full w-full overflow-hidden rounded-xl border border-border-subtle bg-surface-sunken">
            <svg
                ref={svgRef}
                viewBox={`${view.x} ${view.y} ${view.width} ${view.height}`}
                role="application"
                aria-label={t("systemicMap.canvas.label")}
                className="h-full w-full touch-none text-content-primary"
                onPointerMove={continueDrag}
                onPointerUp={endDrag}
                onPointerLeave={leaveBoard}
                onWheel={onWheel}
            >
                <ArrowMarkers idPrefix={markerPrefix} />

                {/* The board's own background.
                    Without it the SVG paints nothing between the elements, so
                    a click on empty space falls through to whatever is
                    underneath: the selection never clears, and there is nothing
                    to take hold of in order to pan.

                    Drawn well past the viewBox because the SVG letterboxes —
                    it preserves its aspect ratio, so a box exactly the size of
                    the view leaves dead bands at the edges of the element
                    where dragging would do nothing. */}
                <rect
                    x={view.x - view.width}
                    y={view.y - view.height}
                    width={view.width * 3}
                    height={view.height * 3}
                    fill="transparent"
                    data-testid="board-backdrop"
                    className="cursor-grab active:cursor-grabbing"
                    onPointerDown={startPan}
                    onClick={() => onSelect(null)}
                />

                {/* Households sit behind everything: they enclose, they do not
                    overlay. */}
                <g className="text-content-tertiary">
                    {state.households.map((household) => (
                        <HouseholdBoundary
                            key={household.id}
                            points={householdPoints(household.members)}
                            label={household.label}
                        />
                    ))}
                </g>

                {/* Children are drawn as sibships, one per couple: a single
                    line under them with each child hanging from it, which is
                    what makes brothers and sisters read as one set. */}
                <g>
                    {sibships.map(({ anchor, members }, index) => (
                        <Sibship key={`sibship-${index}`} anchor={anchor} members={members} />
                    ))}
                </g>

                <g>
                    {state.links.map((link) => {
                        if (link.type === "child") return null;
                        const ends = linkEndpoints(link, nodesById, linksById);
                        if (!ends) return null;
                        const [a, b] = ends;
                        const isSelected = selected === link.id;
                        // A tie is only visible between the two rims, and the
                        // household's rim can be wide: the midpoint of the two
                        // centres sits inside the ring, which is painted over
                        // it and takes the click. The handle goes on the part
                        // that is actually drawn.
                        const [tail, head] =
                            link.type === "system"
                                ? trimSegment(
                                      a,
                                      b,
                                      gapAt(link.from, nodesById),
                                      gapAt(link.to, nodesById),
                                  )
                                : [a, b];
                        const handle = grabPoint(link, tail, head);

                        return (
                            <g
                                key={link.id}
                                style={
                                    isSelected ? undefined : { color: inkOf(link.colour) }
                                }
                                className={
                                    isSelected ? "text-accent" : "text-content-secondary"
                                }
                            >
                                {link.type === "union" && (
                                    <UnionLine
                                        a={a}
                                        b={b}
                                        union={link.union}
                                        since={link.since}
                                        until={link.until}
                                    />
                                )}
                                {link.type === "emotional" && (
                                    <EmotionalLine a={a} b={b} kind={link.emotional} />
                                )}
                                {link.type === "system" &&
                                    (() => {
                                        // The arrowheads point at the roles —
                                        // person or system — rather than at
                                        // whichever end was picked first.
                                        const fromIsCentre = link.from === CENTRE_NODE_ID;
                                        const towardCentre = link.energy !== "towardSystem";
                                        const towardSystem = link.energy !== "towardPerson";
                                        return (
                                            <SystemTie
                                                a={tail}
                                                b={head}
                                                strength={link.strength}
                                                arrowAtA={
                                                    fromIsCentre ? towardCentre : towardSystem
                                                }
                                                arrowAtB={
                                                    fromIsCentre ? towardSystem : towardCentre
                                                }
                                                markerStart={`${markerPrefix}-start`}
                                                markerEnd={`${markerPrefix}-end`}
                                            />
                                        );
                                    })()}
                                {/* The grab point, on the line itself.
                                    A relationship is drawn as a shape with a
                                    hollow middle — a union is a U — so an
                                    invisible ribbon along it, or worse the
                                    middle of its bounding box, aims at empty
                                    board. The handle sits where a person
                                    would point: on the line. */}
                                <circle
                                    cx={handle.x}
                                    cy={handle.y}
                                    r={18}
                                    fill="transparent"
                                    tabIndex={0}
                                    role="button"
                                    aria-label={linkLabel(link)}
                                    data-testid={`link-${link.id}`}
                                    onClick={() => onSelect(link.id)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            event.preventDefault();
                                            onSelect(link.id);
                                        }
                                    }}
                                    className="cursor-pointer outline-none"
                                />
                                {isSelected && (
                                    <circle
                                        cx={handle.x}
                                        cy={handle.y}
                                        r={5}
                                        className="fill-current"
                                    />
                                )}
                            </g>
                        );
                    })}
                </g>

                <g>
                    {state.nodes.map((node) => {
                        const isSelected = selected === node.id;
                        const isLinkTarget = linking === node.id;
                        const hand = otherHand(node);
                        return (
                            <g
                                key={node.id}
                                transform={`translate(${node.x} ${node.y})`}
                                tabIndex={0}
                                role="button"
                                aria-label={nodeLabel(node)}
                                aria-pressed={isSelected}
                                data-testid={`node-${node.id}`}
                                onPointerDown={(event) => startDrag(event, node)}
                                onClick={() => onSelect(node.id)}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        onSelect(node.id);
                                        return;
                                    }
                                    nudge(event, node);
                                }}
                                style={
                                    isSelected || isLinkTarget
                                        ? undefined
                                        : { color: inkOf(node.colour) }
                                }
                                className={`cursor-pointer outline-none ${
                                    isSelected || isLinkTarget
                                        ? "text-accent"
                                        : "text-content-primary"
                                }`}
                            >
                                {(isSelected || isLinkTarget) &&
                                    (isNote(node) ? (
                                        <NoteOutline note={node} />
                                    ) : (
                                        <circle
                                            r={NODE_RADIUS + 12}
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth={2}
                                            strokeDasharray={isLinkTarget ? "4 4" : undefined}
                                        />
                                    ))}
                                {isPerson(node) && (
                                    <>
                                        <IndexRing person={node} />
                                        <PersonShape
                                    person={node}
                                    filled={miscarried.has(node.id)}
                                />
                                        <DeceasedCross person={node} />
                                    </>
                                )}
                                {node.id === CENTRE_NODE_ID && isCentre(node) && (
                                    <CentreShape
                                        radius={
                                            sizing?.id === node.id
                                                ? sizing.radius
                                                : node.radius
                                        }
                                        label={node.label}
                                    />
                                )}
                                {isSystem(node) && (
                                    <SystemShape
                                        system={node}
                                        radius={
                                            sizing?.id === node.id
                                                ? sizing.radius
                                                : undefined
                                        }
                                    />
                                )}
                                {isNote(node) && <NoteCard note={node} />}

                                {editable &&
                                    onResize &&
                                    isSelected &&
                                    (isCentre(node) || isSystem(node)) && (
                                        // On the edge, where the size is: a
                                        // circle is made bigger by pulling its
                                        // own rim, not by a number in a panel.
                                        // The moves and the release are handled
                                        // here rather than on the board, so a
                                        // pointer that wanders off the canvas
                                        // keeps dragging instead of dropping
                                        // the size halfway.
                                        <g
                                            role="button"
                                            tabIndex={0}
                                            aria-label={t("systemicMap.canvas.resize")}
                                            data-testid={`resize-${node.id}`}
                                            className="cursor-ew-resize outline-none"
                                            onPointerDown={(event) => startResize(event, node)}
                                            onPointerMove={(event) => {
                                                if (continueResize(event)) event.stopPropagation();
                                            }}
                                            onPointerUp={(event) => {
                                                event.stopPropagation();
                                                endResize();
                                            }}
                                            onPointerCancel={endResize}
                                            onLostPointerCapture={endResize}
                                            onClick={(event) => event.stopPropagation()}
                                            onKeyDown={(event) => {
                                                const step =
                                                    event.key === "ArrowRight"
                                                        ? 10
                                                        : event.key === "ArrowLeft"
                                                          ? -10
                                                          : 0;
                                                if (step === 0) return;
                                                event.preventDefault();
                                                // The figure's own handler
                                                // nudges it across the board on
                                                // the arrows; from the rim the
                                                // arrows mean size.
                                                event.stopPropagation();
                                                onResize(node, radiusOf(node) + step);
                                            }}
                                        >
                                            {/* Something to actually hit. The
                                                mark is small on purpose; a
                                                target this size is not. */}
                                            <circle
                                                cx={
                                                    sizing?.id === node.id
                                                        ? sizing.radius
                                                        : radiusOf(node)
                                                }
                                                cy={0}
                                                r={22}
                                                fill="transparent"
                                            />
                                            <circle
                                                cx={
                                                    sizing?.id === node.id
                                                        ? sizing.radius
                                                        : radiusOf(node)
                                                }
                                                cy={0}
                                                r={9}
                                                fill="var(--color-surface-raised, #fff)"
                                                stroke="currentColor"
                                                strokeWidth={2}
                                            />
                                        </g>
                                    )}

                                {!isNote(node) && node.id !== CENTRE_NODE_ID && (
                                    <NodeCaption
                                        node={node}
                                        label={nodeLabel(node)}
                                        radius={
                                            sizing?.id === node.id
                                                ? sizing.radius
                                                : radiusOf(node)
                                        }
                                    />
                                )}
                                {hand && <HandChip name={hand} />}
                            </g>
                        );
                    })}
                </g>
                {/* Everyone else's pointer, last so it is over the board
                    rather than under a figure. */}
                <g aria-hidden="true" data-board-chrome="true">
                    {cursors.map((cursor) => (
                        <RemotePointer
                            key={cursor.id}
                            cursor={cursor}
                            ink={inks?.[cursor.id] ?? PRESENCE_INKS[0]}
                        />
                    ))}
                </g>
            </svg>

            <div className="absolute bottom-3 right-3 flex items-center gap-1 rounded-xl border border-border-subtle bg-surface-raised p-1 shadow-theme-md">
                <ZoomButton
                    label={t("systemicMap.canvas.zoomOut")}
                    onClick={() => setScale((s) => Math.max(MIN_SCALE, s / ZOOM_STEP))}
                >
                    <Minus size={16} aria-hidden="true" />
                </ZoomButton>
                <span
                    className="min-w-12 text-center text-xs font-medium tabular-nums text-content-secondary"
                    aria-hidden="true"
                >
                    {Math.round(scale * 100)}%
                </span>
                <ZoomButton
                    label={t("systemicMap.canvas.zoomIn")}
                    onClick={() => setScale((s) => Math.min(MAX_SCALE, s * ZOOM_STEP))}
                >
                    <Plus size={16} aria-hidden="true" />
                </ZoomButton>
                <ZoomButton
                    label={t("systemicMap.canvas.recentre")}
                    onClick={() => {
                        setBase(fitViewBox(state.nodes));
                        setScale(1);
                        setPan({ x: 0, y: 0 });
                    }}
                >
                    <Maximize size={16} aria-hidden="true" />
                </ZoomButton>
            </div>
        </div>
    );
}

/**
 * Where a link can be grabbed: a point on the line it actually draws.
 *
 * A union hangs below its partners and a child link steps down and across, so
 * neither passes anywhere near the straight line between its two endpoints —
 * which is where a naive hit area, and a click aimed at the middle of the
 * bounding box, both land.
 */
function grabPoint(link: MapLink, a: Point, b: Point): Point {
    if (link.type === "union") return unionGeometry(a, b).anchor;
    if (link.type === "child") {
        const path = childPath(a, b);
        return { x: (path[1].x + path[2].x) / 2, y: path[1].y };
    }
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/**
 * The text under a figure.
 *
 * Names and years live in the SVG text layer rather than in an HTML overlay so
 * they pan and zoom with the element they belong to, and so a screen reader
 * meets them in the order the board is built.
 */
function NodeCaption({
    node,
    label,
    radius,
}: {
    node: MapNode;
    label: string;
    /** How far down the caption starts: under the shape, whatever its size. */
    radius: number;
}) {
    const lines: string[] = [];
    if (isPerson(node)) {
        if (node.name) lines.push(node.name);
        const years = [node.birthYear, node.deathYear].filter(Boolean);
        if (years.length) lines.push(years.join("–"));
    } else {
        lines.push(label);
    }

    return (
        <g aria-hidden="true" fill="currentColor">
            {lines.map((line, index) => (
                <text
                    key={index}
                    x={0}
                    y={radius + 18 + index * 15}
                    textAnchor="middle"
                    fontSize={13}
                    fontWeight={500}
                >
                    {line}
                </text>
            ))}
        </g>
    );
}

/**
 * Somebody else's pointer.
 *
 * An arrow with their name beside it, the way every shared canvas draws one —
 * the name is the point: two anonymous arrows on a family tree tell you less
 * than none.
 */
function RemotePointer({ cursor, ink }: { cursor: RemoteCursor; ink: string }) {
    const { t } = useTranslation();
    // Never a blank chip: a nameless pointer is still someone, and an empty
    // pill beside an arrow reads as a rendering fault rather than as a person.
    const label =
        cursor.name.trim() ||
        t(
            cursor.kind === "therapist"
                ? "systemicMap.canvas.byTherapist"
                : "systemicMap.canvas.byPatient",
        );
    const width = Math.max(30, label.length * 7 + 14);
    return (
        <g
            // A CSS transform rather than the attribute, so it can be eased:
            // positions arrive a few times a second, and a pointer that jumps
            // between them reads as a laggy connection rather than as someone
            // moving. The glide is exactly one sample long, so it never lags
            // behind the truth by more than it would have jumped.
            style={{
                transform: `translate(${cursor.x}px, ${cursor.y}px)`,
                transition: "transform 90ms linear",
                // Their own colour, not their role's: in a family session
                // three participants used to share one, which answers
                // "someone is moving that" but not "who".
                color: ink,
            }}
            className="motion-reduce:transition-none"
        >
            <path
                d="M0 0 L0 16 L4.2 12.2 L7 18 L9.6 16.6 L6.8 11 L12 10.6 Z"
                className="fill-current"
                stroke="white"
                strokeWidth={1}
                strokeLinejoin="round"
            />
            <rect x={12} y={12} rx={7} width={width} height={16} className="fill-current" />
            <text
                x={12 + width / 2}
                y={24}
                textAnchor="middle"
                className="fill-white text-xs font-semibold"
            >
                {label}
            </text>
        </g>
    );
}

/** Who last touched this, when it was not you. */
function HandChip({ name }: { name: string }) {
    const width = Math.max(34, name.length * 7 + 14);
    return (
        <g
            aria-hidden="true"
            // Live-collaboration chrome, not part of the drawing: it is taken
            // out before the board is filed as an image.
            data-board-chrome="true"
            transform={`translate(${NODE_RADIUS - 4} ${-NODE_RADIUS - 22})`}
        >
            <rect x={0} y={0} rx={8} width={width} height={17} className="fill-accent" />
            <text
                x={width / 2}
                y={12.5}
                textAnchor="middle"
                className="fill-white text-xs font-semibold"
            >
                {name}
            </text>
        </g>
    );
}

function ZoomButton({
    label,
    onClick,
    children,
}: {
    label: string;
    onClick: () => void;
    children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            title={label}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-content-secondary transition-colors duration-fast hover:bg-surface-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
            {children}
        </button>
    );
}
